import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button, Spin, message } from "antd";
import "../HotelProfile/Components/MarketingOfferPreviewModal.css";
import {
  fetchPublicOfferByToken,
  postPublicOfferInterest,
} from "../../../services/publicOfferLinksApi";
import { resolveAgentCompanyLogoUrl } from "../../../utils/resolveAgentCompanyLogoUrl";
import fallbackHotelImage from "../../../assets/images/flight-offer-hero.png";
import HotelOfferSaleHero from "../../../Components/HotelOfferSale/HotelOfferSaleHero";
import "../../../Components/Agent/OrderSummery/hotelOfferGuestSale.css";
import "../../../Components/HotelOfferSale/hotelOfferSalePage.css";
import "./PublicOfferLinkPage.css";
import "./organizedTourPublicOffer.css";
import "./organizedTourPublicOfferFlights.css";
import "./organizedTourPublicOfferRooms.css";
import "./organizedTourItinerary.css";
import OrganizedTourPublicOfferLanding from "./OrganizedTourPublicOfferLanding";
import PublicOfferHeroTemplate from "./PublicOfferHeroTemplate";
import CampaignCustomerOfferView from "../../../Components/campaigns/CampaignCustomerOfferView";
import { hydrateDynamicPackageCustomerOffer } from "../../../utils/dynamicPackage/hydrateDynamicPackageCustomerOffer";
import {
  formatIsraelDisplayDate,
  parseIsraelDisplayDate,
} from "../../../utils/DatesHelpers";
import {
  PUBLIC_OFFER_RENDERERS,
  resolvePublicOfferRenderer,
} from "./resolvePublicOfferRenderer";
import {
  resolvePublicHotelOfferCustomerTotalIls,
  resolvePublicHotelOfferCustomerTotalUsd,
} from "../../../utils/resolvePublicHotelOfferCustomerTotalIls";
import {
  mergeHotelOfferGalleryImages,
  resolveHotelOfferGalleryImagesFromPublicPayload,
} from "../../../utils/resolveHotelOfferGalleryImages";
import {
  fetchHotelOfferGalleryUrlsByHotelId,
  resolveHotelOfferGalleryHotelId,
} from "../../../utils/fetchHotelOfferGalleryUrlsByHotelId";
import HotelOfferSaleGallery from "../../../Components/HotelOfferSale/HotelOfferSaleGallery";
import "../../../utils/pricing/hotelApiPriceDualLineDisplay.css";

const LEGAL_NOTE =
  "הצגה זו מבוססת על פרטים שנבחרו על ידי נציג בזמן יצירת הקישור. המחיר והזמינות אינם מהווים התחייבות עד להשלמת הזמנה מול הספקים.";

function formatIls(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `₪${Math.round(n).toLocaleString("he-IL")}`;
}

function PublicOfferAgentContactFooter({
  branding,
  title = "לפרטים והזמנות ניתן ליצור קשר עם הסוכן",
}) {
  if (!branding) return null;
  const hasContact = Boolean(branding.phone || branding.email || branding.whatsapp);
  const hasIdentity = Boolean(
    branding.logoUrl || branding.agencyName || branding.agentName
  );
  if (!hasContact && !hasIdentity) return null;

  const identityLine = [
    branding.agentName || null,
    branding.agencyName &&
    branding.agentName &&
    String(branding.agencyName).trim() !== String(branding.agentName).trim()
      ? branding.agencyName
      : !branding.agentName
        ? branding.agencyName
        : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <footer
      className="public-offer-compare-contact public-offer-hotel-agent-contact"
      dir="rtl"
      aria-label="פרטי הסוכן"
    >
      {branding.logoUrl ? (
        <div className="public-offer-compare-branding__logo-wrap">
          <img
            src={branding.logoUrl}
            alt={branding.agencyName || branding.agentName || "לוגו סוכן"}
            className="public-offer-compare-branding__logo"
            loading="lazy"
          />
        </div>
      ) : null}
      <p className="public-offer-compare-contact__title">{title}</p>
      {identityLine ? (
        <p className="public-offer-hotel-agent-contact__identity">{identityLine}</p>
      ) : null}
      {hasContact ? (
        <div className="public-offer-compare-contact__rows">
          {branding.phone ? (
            <a
              href={`tel:${String(branding.phone).replace(/[^\d+]/g, "")}`}
              className="public-offer-compare-contact__link"
            >
              טלפון: {branding.phone}
            </a>
          ) : null}
          {branding.email ? (
            <a
              href={`mailto:${branding.email}`}
              className="public-offer-compare-contact__link"
            >
              מייל: {branding.email}
            </a>
          ) : null}
          {branding.whatsapp ? (
            <a
              href={`https://wa.me/${String(branding.whatsapp).replace(/[^\d]/g, "")}`}
              target="_blank"
              rel="noreferrer"
              className="public-offer-compare-contact__link"
            >
              וואטסאפ: {branding.whatsapp}
            </a>
          ) : null}
        </div>
      ) : null}
    </footer>
  );
}

function normalizeBranding(rawBranding, fallbackAgent = null) {
  const b = rawBranding && typeof rawBranding === "object" ? rawBranding : {};
  const a = fallbackAgent && typeof fallbackAgent === "object" ? fallbackAgent : {};
  const first = String(a.firstname || a.firstName || "").trim();
  const last = String(a.lastname || a.lastName || "").trim();
  const agentNameFromAgent = [first, last].filter(Boolean).join(" ").trim() || null;

  const out = {
    logoUrl:
      String(b.logoUrl || "").trim() ||
      resolveAgentCompanyLogoUrl(a) ||
      null,
    agencyName:
      String(b.agencyName || b.companyName || "").trim() ||
      String(a.companyname || a.companyName || "").trim() ||
      null,
    agentName:
      String(b.agentName || b.contactName || "").trim() ||
      agentNameFromAgent,
    phone:
      String(b.phone || "").trim() ||
      String(a.cell || a.phone || a.Phone || "").trim() ||
      null,
    email:
      String(b.email || "").trim() ||
      String(a.email || a.Email || "").trim() ||
      null,
    whatsapp:
      String(b.whatsapp || "").trim() ||
      String(a.whatsapp || a.WhatsApp || "").trim() ||
      null,
  };
  const hasAny = Object.values(out).some(Boolean);
  return hasAny ? out : null;
}

function resolveCustomerDetails(payload, viewModel) {
  const customerPayload =
    payload?.customer && typeof payload.customer === "object" ? payload.customer : {};
  const vm = viewModel && typeof viewModel === "object" ? viewModel : {};
  return {
    customerName:
      String(customerPayload.name || "").trim() ||
      String(vm.customer_name || "").trim() ||
      null,
    customerPhone:
      String(customerPayload.phone || "").trim() ||
      String(vm.customer_phone || "").trim() ||
      null,
    customerEmail:
      String(customerPayload.email || "").trim() ||
      String(vm.customer_email || "").trim() ||
      null,
  };
}

function formatOfferExpiryHe(isoLike) {
  if (!isoLike) return null;
  try {
    const d = new Date(isoLike);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString("he-IL", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return null;
  }
}

function toText(...values) {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
}

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function resolvePublicOfferPayload(data) {
  const root = data && typeof data === "object" ? data : {};
  const direct = root.payload && typeof root.payload === "object" ? root.payload : null;
  if (direct?.public_view_model || direct?.offer_type || direct?.productType) {
    return direct;
  }
  const nested =
    direct?.payload && typeof direct.payload === "object" ? direct.payload : null;
  if (nested?.public_view_model || nested?.offer_type || nested?.productType) {
    return nested;
  }
  if (root.public_view_model || root.offer_type || root.productType) {
    return root;
  }
  return direct || nested || {};
}

function resolvePublicViewModel(payload) {
  if (payload?.public_view_model && typeof payload.public_view_model === "object") {
    return payload.public_view_model;
  }
  if (payload && typeof payload === "object") {
    return payload;
  }
  return null;
}

function parseDateRange(rawLine) {
  const line = String(rawLine || "").trim();
  if (!line) return { checkIn: null, checkOut: null };
  const split = line
    .split(/[-–—]/)
    .map((part) => String(part || "").trim())
    .filter(Boolean);
  if (split.length >= 2) return { checkIn: split[0], checkOut: split[1] };
  return { checkIn: split[0] || null, checkOut: null };
}

function parseOfferDate(value) {
  return parseIsraelDisplayDate(value);
}

function resolveNights(checkIn, checkOut) {
  const inDate = parseOfferDate(checkIn);
  const outDate = parseOfferDate(checkOut);
  if (!inDate || !outDate) return null;
  const nights = Math.round((outDate - inDate) / (1000 * 60 * 60 * 24));
  return Number.isFinite(nights) && nights > 0 ? nights : null;
}

function pickImageValue(value) {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) {
    for (const item of value) {
      const resolved = pickImageValue(item);
      if (resolved) return resolved;
    }
    return "";
  }
  if (typeof value === "object") {
    return toText(
      value.url,
      value.src,
      value.image,
      value.imageUrl,
      value.thumbnail,
      value.href
    );
  }
  return "";
}

function collectImageValues(...values) {
  const out = [];
  const push = (value) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach(push);
      return;
    }
    const image = pickImageValue(value);
    if (image && !out.includes(image)) out.push(image);
  };
  values.forEach(push);
  return out;
}

function pickHotelImage(offer) {
  const payload = offer && typeof offer === "object" ? offer : {};
  const vm = payload.public_view_model && typeof payload.public_view_model === "object"
    ? payload.public_view_model
    : payload;
  const candidates = [
    ["offer.hotel.mainImage", payload?.hotel?.mainImage],
    ["offer.hotel.image", payload?.hotel?.image],
    ["offer.hotel.thumbnail", payload?.hotel?.thumbnail],
    ["offer.hotel.images[0]", payload?.hotel?.images?.[0]],
    ["offer.packageSnapshot.hotel.image", payload?.packageSnapshot?.hotel?.image],
    ["offer.order_response.hotel.image", payload?.order_response?.hotel?.image],
    ["public_view_model.hotel.mainImage", vm?.hotel?.mainImage],
    ["public_view_model.hotel.image", vm?.hotel?.image],
    ["public_view_model.hotel.thumbnail", vm?.hotel?.thumbnail],
    ["public_view_model.hotel.images[0]", vm?.hotel?.images?.[0]],
    ["public_view_model.heroImage", vm?.heroImage],
    ["public_view_model.heroImageUrl", vm?.heroImageUrl],
    ["public_view_model.hotelImage", vm?.hotelImage],
  ];

  for (const [source, value] of candidates) {
    const image = pickImageValue(value);
    if (image) return { hotelImage: image, imageSource: source };
  }
  return { hotelImage: fallbackHotelImage, imageSource: "fallback_static" };
}

export function resolvePublicHotelOfferViewModel(offer) {
  const payload = offer && typeof offer === "object" ? offer : {};
  const vm = payload.public_view_model && typeof payload.public_view_model === "object"
    ? payload.public_view_model
    : {};
  const selection = payload.selection && typeof payload.selection === "object" ? payload.selection : {};
  const selectedPackage =
    selection.selected_package && typeof selection.selected_package === "object"
      ? selection.selected_package
      : {};
  const selectedRoom =
    selection.selected_room && typeof selection.selected_room === "object"
      ? selection.selected_room
      : {};
  const searchPhrase =
    selection.search_phrase && typeof selection.search_phrase === "object"
      ? selection.search_phrase
      : {};
  const hotel =
    payload.hotel && typeof payload.hotel === "object"
      ? payload.hotel
      : vm.hotel && typeof vm.hotel === "object"
        ? vm.hotel
        : {};
  const room = vm.room && typeof vm.room === "object" ? vm.room : {};
  const price = vm.price && typeof vm.price === "object" ? vm.price : {};
  const pricing = vm.pricing && typeof vm.pricing === "object" ? vm.pricing : {};
  const galleryImages = resolveHotelOfferGalleryImagesFromPublicPayload(payload);
  const { hotelImage: fallbackHotelImage, imageSource } = pickHotelImage(payload);
  const hotelImage = galleryImages[0] || fallbackHotelImage;

  const hotelName = toText(
    hotel.HotelName,
    hotel.hotelName,
    hotel.name,
    vm.hotelName,
    vm.header?.headlineHe,
    vm.title
  );
  const roomName = toText(
    room.name,
    room.roomName,
    vm.roomName,
    selectedRoom.roomName,
    selectedRoom.name,
    selection.room_line_display,
    selectedPackage.roomName
  );
  const board = toText(
    vm.boardBasis,
    vm.boardLabel,
    hotel.boardLabel,
    selectedPackage.boardLabel,
    selection.board_label,
    vm.board,
    room.board
  );
  const checkIn = toText(
    vm.header?.checkIn,
    vm.search_phrase?.checkIn,
    searchPhrase.checkIn,
    payload.check_in,
    payload.checkin
  );
  const checkOut = toText(
    vm.header?.checkOut,
    vm.search_phrase?.checkOut,
    searchPhrase.checkOut,
    payload.check_out,
    payload.checkout
  );
  const parsedDates = parseDateRange(toText(vm.dates, vm.header?.datesLine, vm.dateRange));
  const rawCheckIn = checkIn || parsedDates.checkIn || "";
  const rawCheckOut = checkOut || parsedDates.checkOut || "";
  const finalCheckIn = formatIsraelDisplayDate(rawCheckIn) || rawCheckIn;
  const finalCheckOut = formatIsraelDisplayDate(rawCheckOut) || rawCheckOut;
  const dates =
    finalCheckIn && finalCheckOut
      ? `${finalCheckIn} - ${finalCheckOut}`
      : toText(vm.dates, vm.header?.datesLine, vm.dateRange);
  const passengers = toText(
    vm.passengers,
    vm.occupancy,
    room.occupancyLabel,
    vm.occupancyLabel,
    selectedRoom.occupancyLabel
  );
  const currency = String(
    vm.currency ||
      price.currency ||
      pricing.currency ||
      payload.currency ||
      "ILS"
  ).toUpperCase();
  const handlingFee =
    toNumber(vm.handlingFee) ??
    toNumber(vm.handling_fee) ??
    toNumber(pricing.handlingFee) ??
    toNumber(pricing.agentFeeIls) ??
    toNumber(price.handlingFee) ??
    toNumber(payload.handling_fee_ils) ??
    0;
  const customerTotalIls = resolvePublicHotelOfferCustomerTotalIls(payload);
  const customerTotalUsd = resolvePublicHotelOfferCustomerTotalUsd(payload);
  const totalPrice = customerTotalIls;
  const totalPriceUsd = customerTotalUsd;
  const basePrice =
    toNumber(vm.hotelTotalIls) ??
    toNumber(pricing.hotelTotalIls) ??
    toNumber(payload.hotel_total_ils) ??
    toNumber(vm.basePrice) ??
    toNumber(vm.base_price) ??
    toNumber(pricing.basePrice) ??
    toNumber(price.baseAmount) ??
    (customerTotalIls != null && handlingFee > 0
      ? customerTotalIls - handlingFee
      : customerTotalIls);
  const branding = normalizeBranding(
    vm.branding || payload.branding,
    payload.agent || payload.order?.agent || null
  );

  return {
    hotelName: hotelName || "הצעת מלון",
    hotelImage,
    imageSource,
    galleryImages: galleryImages.length ? galleryImages : [hotelImage].filter(Boolean),
    roomName: roomName || "חדר נבחר",
    board: board || "—",
    checkIn: finalCheckIn,
    checkOut: finalCheckOut,
    nights: resolveNights(finalCheckIn, finalCheckOut),
    dates: dates || "לפי תאריכי ההצעה",
    passengers: passengers || "לפי הרכב הנוסעים",
    basePrice,
    handlingFee,
    totalPrice,
    totalPriceUsd,
    currency,
    agent: branding,
    destination: toText(
      vm.destination,
      hotel.city,
      hotel.City,
      hotel.CityName,
      hotel.locationLine,
      searchPhrase.city,
      searchPhrase.destination
    ),
    rating: toNumber(vm.rating ?? vm.stars ?? hotel.Rating ?? hotel.starsCount),
    legalNote: toText(vm.legalNote, vm.disclaimer, LEGAL_NOTE),
    description: toText(
      vm.description,
      vm.hotelDescription,
      hotel.description,
      hotel.Description,
      payload?.hotelDescription,
      payload?.description
    ),
    cancellationPolicy: toText(
      vm.cancellationPolicy,
      vm.cancellation,
      selectedPackage.cancellationPolicy,
      selectedPackage.refundability
        ? `ניתן לבטל עד ${selectedPackage.refundableUntil || "המועד המצוין בהזמנה"}.`
        : ""
    ),
    tags: [
      roomName || null,
      board || null,
      passengers || null,
      totalPrice != null ? formatIls(totalPrice) : null,
    ].filter(Boolean),
  };
}

export default function PublicOfferLinkPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [vm, setVm] = useState(null);
  const [offerPayload, setOfferPayload] = useState(null);
  /** תוקף לינק מתשובת GET (כול ההצעות; משמש מאורגנים בסיידבר) */
  const [offerExpiresAt, setOfferExpiresAt] = useState(null);
  const [interestBusy, setInterestBusy] = useState(false);
  const [interestDone, setInterestDone] = useState(false);
  const [interestError, setInterestError] = useState(null);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [selectingPackageId, setSelectingPackageId] = useState(null);
  const [compareSelectionNotice, setCompareSelectionNotice] = useState("");
  const [remoteGalleryImages, setRemoteGalleryImages] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      setVm(null);
      setOfferPayload(null);
      setOfferExpiresAt(null);
      setSelectedPackageId(null);
      setSelectingPackageId(null);
      setCompareSelectionNotice("");
      setRemoteGalleryImages(null);
      try {
        const data = await fetchPublicOfferByToken(token);
        if (cancelled) return;
        if (!data?.success) {
          const msg =
            data?.error?.message ||
            (data?.error?.code === "EXPIRED"
              ? "תוקף הקישור פג או שההצעה אינה זמינה עוד."
              : "לא ניתן לטעון את ההצעה.");
          setErr(msg);
          return;
        }
        const payload = resolvePublicOfferPayload(data);
        const view = resolvePublicViewModel(payload);
        const renderer = resolvePublicOfferRenderer(view, payload);
        if (!view || renderer.renderer === PUBLIC_OFFER_RENDERERS.UNSUPPORTED) {
          setErr("פורמט הצעה לא נתמך.");
          return;
        }
        setOfferPayload(payload);
        setVm(view);
        setOfferExpiresAt(data.expires_at ?? null);
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        const serverMsg = e?.response?.data?.error?.message;
        if (status === 410) {
          setErr(serverMsg || "תוקף הקישור פג או שההצעה אינה זמינה עוד.");
        } else if (status === 404) {
          setErr(serverMsg || "ההצעה לא נמצאה.");
        } else {
          setErr(serverMsg || "שגיאה בטעינת ההצעה. נסו שוב מאוחר יותר.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!offerPayload || !vm) return;
    const renderer = resolvePublicOfferRenderer(vm, offerPayload);
    const needsGalleryEnrichment =
      renderer.renderer === PUBLIC_OFFER_RENDERERS.HOTEL_OFFER ||
      renderer.renderer === PUBLIC_OFFER_RENDERERS.HERO_TEMPLATE_V1;
    if (!needsGalleryEnrichment) return;

    const initial = resolveHotelOfferGalleryImagesFromPublicPayload(offerPayload);
    if (initial.length > 1) {
      setRemoteGalleryImages(initial);
      return;
    }
    const hotelId = resolveHotelOfferGalleryHotelId(offerPayload);
    if (!hotelId) {
      setRemoteGalleryImages(initial);
      return;
    }
    let cancelled = false;
    fetchHotelOfferGalleryUrlsByHotelId(hotelId).then((fetched) => {
      if (cancelled) return;
      setRemoteGalleryImages(
        mergeHotelOfferGalleryImages(initial, fetched).slice(0, 24)
      );
    });
    return () => {
      cancelled = true;
    };
  }, [offerPayload, vm]);

  const title = useMemo(() => {
    if (vm?.version === 2 && vm?.offer_type === "organized_tour") {
      return `${vm?.tour_title_he || "טיול מאורגן"} | HotelsRiver`;
    }
    const normalizedOfferType = String(vm?.offer_type ?? vm?.offerType ?? "")
      .trim()
      .toLowerCase();
    const normalizedProductType = String(vm?.productType ?? "")
      .trim()
      .toLowerCase();
    if (
      normalizedOfferType === "hotel_offer" ||
      normalizedOfferType === "hotel" ||
      normalizedProductType === "hotel"
    ) {
      return `${toText(vm?.hotelName, vm?.hotel?.name, "Hotel Offer")} | HotelsRiver`;
    }
    if (vm?.version === 2 && vm?.offer_type === "package_compare") {
      return "השוואת חבילות נופש | HotelsRiver";
    }
    if (vm?.version === 2 && vm?.offer_type === "hotel_compare") {
      return "השוואת מלונות | HotelsRiver";
    }
    if (!vm?.header?.headlineHe) return "הצעת חבילה";
    return `${vm.header.headlineHe} | HotelsRiver`;
  }, [vm]);

  const onInterest = useCallback(async () => {
    if (!token || interestBusy || interestDone) return;
    setInterestBusy(true);
    setInterestError(null);
    try {
      await postPublicOfferInterest(token);
      setInterestDone(true);
    } catch {
      setInterestDone(true);
    } finally {
      setInterestBusy(false);
    }
  }, [token, interestBusy, interestDone]);

  const onPublicOfferLeadSubmit = useCallback(
    async ({ fullName, phone }) => {
      if (!token || interestBusy || interestDone) return;
      setInterestBusy(true);
      setInterestError(null);
      try {
        const hotelName =
          offerPayload?.public_view_model?.hotel?.name ||
          offerPayload?.public_view_model?.header?.headlineHe ||
          null;
        await postPublicOfferInterest(token, {
          event: "lead_interest",
          full_name: fullName,
          phone,
          customerName: fullName,
          customerPhone: phone,
          interest_destination: hotelName,
          source_url:
            typeof window !== "undefined" ? window.location.href : null,
          category: "dynamic_package_public_offer",
        });
        setInterestDone(true);
      } catch (err) {
        const msg =
          err?.response?.data?.error?.message ||
          "לא הצלחנו לשלוח את הפרטים. נסו שוב.";
        setInterestError(msg);
        throw err;
      } finally {
        setInterestBusy(false);
      }
    },
    [token, interestBusy, interestDone, offerPayload]
  );

  const onRequestAgentMessage = useCallback(
    async (pkg, offerType = "package_compare") => {
      const idKey =
        offerType === "hotel_compare" ? pkg?.hotelId : pkg?.packageId;
      if (!token || !idKey || selectingPackageId) return;
      setSelectingPackageId(idKey);
      const customer = resolveCustomerDetails(offerPayload, vm);
      const interestPayload = {
        event: "agent_message_requested",
        packageId: offerType === "package_compare" ? String(idKey) : null,
        hotelId: offerType === "hotel_compare" ? String(idKey) : null,
        offerType,
        hotelName: pkg?.hotelName || null,
        finalPriceIls: Number(pkg?.finalPriceIls) || 0,
        customerName: customer.customerName,
        customerPhone: customer.customerPhone,
        customerEmail: customer.customerEmail,
      };
      try {
        await postPublicOfferInterest(token, interestPayload);
        setSelectedPackageId(idKey);
        setCompareSelectionNotice(
          "הבקשה נשלחה לסוכן. ניצור איתך קשר בהקדם."
        );
      } catch {
        message.error("לא ניתן לשלוח את הבחירה כרגע. נסו שוב בעוד רגע.");
      } finally {
        setSelectingPackageId(null);
      }
    },
    [token, selectingPackageId, offerPayload, vm]
  );

  const rendererSelection = useMemo(
    () => resolvePublicOfferRenderer(vm, offerPayload),
    [offerPayload, vm]
  );
  const publicHotelVm = useMemo(() => {
    if (rendererSelection.renderer !== PUBLIC_OFFER_RENDERERS.HOTEL_OFFER || !offerPayload) {
      return null;
    }
    const base = resolvePublicHotelOfferViewModel(offerPayload);
    if (!remoteGalleryImages?.length) return base;
    return {
      ...base,
      galleryImages: remoteGalleryImages,
      hotelImage: remoteGalleryImages[0] || base.hotelImage,
    };
  }, [offerPayload, rendererSelection.renderer, remoteGalleryImages]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const route =
      typeof window !== "undefined" ? window.location.pathname : `/offers/public/${token || ""}`;
    if (vm) {
      console.info("[PUBLIC_OFFER_RENDERER_SELECTED]", {
        route,
        renderer: rendererSelection.renderer,
        reason: rendererSelection.reason,
        version: vm?.version ?? null,
        offerType: vm?.offer_type ?? null,
      });
      const offerType =
        vm?.offer_type ??
        vm?.offerType ??
        offerPayload?.offer_type ??
        offerPayload?.offerType ??
        null;
      const template =
        vm?.template ??
        vm?.templateType ??
        offerPayload?.template ??
        rendererSelection.renderer;
      const selectedComponent =
        rendererSelection.renderer === PUBLIC_OFFER_RENDERERS.HOTEL_OFFER
          ? "HotelOfferSaleHero"
          : rendererSelection.renderer === PUBLIC_OFFER_RENDERERS.ORGANIZED_TOUR_V2
            ? "OrganizedTourPublicOfferLanding"
            : rendererSelection.renderer === PUBLIC_OFFER_RENDERERS.HERO_TEMPLATE_V1
              ? "PublicOfferHeroTemplate"
              : rendererSelection.renderer === PUBLIC_OFFER_RENDERERS.COMPARE_V2
                ? "PublicOfferLinkPageCompare"
                : "Unsupported";
      console.info("[PUBLIC_OFFER_TEMPLATE_RESOLVE_TRACE]", {
        token: token || null,
        offerType,
        template,
        selectedComponent,
        fallbackReason:
          rendererSelection.renderer === PUBLIC_OFFER_RENDERERS.HOTEL_OFFER &&
          selectedComponent !== "HotelOfferSaleHero"
            ? rendererSelection.reason || "hotel_offer_not_mapped_to_sale_hero"
            : null,
      });
    }
    if (rendererSelection.renderer === PUBLIC_OFFER_RENDERERS.HOTEL_OFFER || publicHotelVm) {
      console.info("[PUBLIC_HOTEL_OFFER_PAGE_TRACE]", {
        token: token || null,
        pathname: route,
        loadingState: loading ? "loading" : err ? "error" : "loaded",
        offerLoaded: Boolean(offerPayload && vm),
        hotelName: publicHotelVm?.hotelName || null,
        resolvedHotelImage: publicHotelVm?.hotelImage || null,
        imageSource: publicHotelVm?.imageSource || null,
        totalPrice: publicHotelVm?.totalPrice ?? null,
        customerTotalIncludesHandlingFee: true,
        renderMode: "HotelOfferSaleHero",
        usingLegacyFallback: false,
      });
    }
    if (rendererSelection.renderer === PUBLIC_OFFER_RENDERERS.HERO_TEMPLATE_V1) {
      console.info("[PUBLIC_OFFER_LEGACY_RENDER_BLOCKED]", {
        route,
        blockedRenderer: "legacy_v1_plain_sections",
        activeRenderer: "hero_template_v1",
      });
      console.info("[PUBLIC_OFFER_HERO_TEMPLATE_ACTIVE]", {
        route,
        hasHeroImage: Boolean(vm?.heroImageUrl),
        headline: vm?.header?.headlineHe || null,
      });
    }
    if (rendererSelection.renderer === PUBLIC_OFFER_RENDERERS.HOTEL_OFFER) {
      console.info("[HOTEL_PUBLIC_TEMPLATE_OVERRIDE]", {
        route,
        resolvedRenderer: "HotelOfferSaleHero",
        blockedRenderer: "OrderSummery",
        offerType: vm?.offer_type ?? vm?.offerType ?? null,
        productType: vm?.productType ?? null,
      });
    }
  }, [
    err,
    loading,
    offerPayload,
    publicHotelVm?.hotelImage,
    publicHotelVm?.hotelName,
    publicHotelVm?.imageSource,
    publicHotelVm?.totalPrice,
    rendererSelection.reason,
    rendererSelection.renderer,
    token,
    vm?.offerType,
    vm?.offer_type,
    vm?.productType,
  ]);

  if (loading) {
    return (
      <div className="public-offer-page public-offer-page--center" dir="rtl">
        <Spin size="large" tip="טוען הצעה…" />
      </div>
    );
  }

  if (err || !vm) {
    return (
      <div className="public-offer-page public-offer-page--center" dir="rtl">
        <Helmet>
          <title>הצעה לא זמינה | HotelsRiver</title>
        </Helmet>
        <div className="public-offer-page__card public-offer-page__card--error">
          <h1 className="public-offer-page__error-title">
            ההצעה אינה זמינה או פג תוקפה
          </h1>
          <p className="public-offer-page__error-text">
            {err || "לא ניתן להציג את העמוד."}
          </p>
        </div>
      </div>
    );
  }

  if (rendererSelection.renderer === PUBLIC_OFFER_RENDERERS.ORGANIZED_TOUR_V2) {
    const branding = normalizeBranding(
      vm.branding || offerPayload?.branding,
      offerPayload?.agent || offerPayload?.order?.agent || null
    );
    const cust = resolveCustomerDetails(offerPayload, vm);
    const expiryLabel = formatOfferExpiryHe(offerExpiresAt);
    const primaryAgentTitle =
      (branding?.agentName && String(branding.agentName).trim()) ||
      (branding?.agencyName && String(branding.agencyName).trim()) ||
      null;
    const showAgencySubline =
      branding?.agentName &&
      branding?.agencyName &&
      String(branding.agencyName).trim() !== String(branding.agentName).trim();

    return (
      <div className="public-offer-page org-tour-offer-shell" dir="rtl">
        <Helmet>
          <title>{title}</title>
          <meta name="description" content="הצעת טיול מאורגן" />
        </Helmet>
        <OrganizedTourPublicOfferLanding
          vm={vm}
          branding={branding}
          cust={cust}
          expiryLabel={expiryLabel}
          formatIls={formatIls}
          onInterest={onInterest}
          interestBusy={interestBusy}
          interestDone={interestDone}
          primaryAgentTitle={primaryAgentTitle}
          showAgencySubline={showAgencySubline}
        />
      </div>
    );
  }

  if (rendererSelection.renderer === PUBLIC_OFFER_RENDERERS.COMPARE_V2) {
    const isHotelCompare = vm.offer_type === "hotel_compare";
    const compareItems = isHotelCompare
      ? Array.isArray(vm.hotels)
        ? vm.hotels.slice(0, 4)
        : []
      : Array.isArray(vm.packages)
        ? vm.packages.slice(0, 4)
        : [];
    const branding = normalizeBranding(
      vm.branding || offerPayload?.branding,
      offerPayload?.agent || offerPayload?.order?.agent || null
    );
    return (
      <div className="public-offer-page" dir="rtl">
        <Helmet>
          <title>{title}</title>
          <meta
            name="description"
            content="השוואת חבילות נופש ללקוח, כולל מחיר סופי לכל חבילה."
          />
        </Helmet>

        <article className="public-offer-page__article public-offer-compare-page__article">
          <header className="public-offer-page__header public-offer-compare-page__header">
            {branding?.logoUrl ? (
              <div className="public-offer-compare-branding__logo-wrap">
                <img
                  src={branding.logoUrl}
                  alt={branding.agencyName || branding.agentName || "לוגו סוכן"}
                  className="public-offer-compare-branding__logo"
                  loading="lazy"
                />
              </div>
            ) : null}
            {branding?.agencyName || branding?.agentName ? (
              <div className="public-offer-compare-branding__title">
                {branding?.agencyName || branding?.agentName}
              </div>
            ) : null}
            <p className="moffer-preview__kicker">הצעה מותאמת אישית</p>
            <h1 className="moffer-preview__headline">
              {isHotelCompare ? "השוואת מלונות" : "השוואת חבילות נופש"}
            </h1>
            <p className="public-offer-compare-page__subtitle">
              {vm.customer_name
                ? `${vm.customer_name}, בחר/י את ${
                    isHotelCompare ? "המלון" : "החבילה"
                  } שמתאים לך`
                : `בחרו את ${isHotelCompare ? "המלון" : "החבילה"} שמתאים לכם`}
            </p>
          </header>

          <div className="public-offer-compare-grid">
            {compareItems.map((pkg) => (
              <section
                className="public-offer-compare-card"
                key={pkg.packageId || pkg.hotelId || pkg.hotelName}
              >
                {pkg.image ? (
                  <div className="public-offer-compare-card__image-wrap">
                    <img
                      src={pkg.image}
                      alt={pkg.hotelName || "חבילה"}
                      className="public-offer-compare-card__image"
                      loading="lazy"
                    />
                  </div>
                ) : null}
                <div className="public-offer-compare-card__body">
                  <h3 className="public-offer-compare-card__title">
                    {pkg.hotelName || "חבילה"}
                  </h3>

                  <div className="public-offer-compare-card__rows">
                    <div><strong>יעד:</strong> {pkg.destination || "—"}</div>
                    <div><strong>תאריכים:</strong> {pkg.dates || "—"}</div>
                    <div><strong>לילות:</strong> {pkg.nights || "—"}</div>
                    <div><strong>בסיס אירוח:</strong> {pkg.board || "—"}</div>
                    {!isHotelCompare ? (
                      <>
                        <div><strong>סטטוס אישור:</strong> {pkg.confirmationStatus || "—"}</div>
                        <div><strong>טיסה:</strong> {pkg.flightRoute || "—"}</div>
                        <div><strong>שעות:</strong> {pkg.flightTimes || "—"}</div>
                        <div><strong>חברת תעופה:</strong> {pkg.airlineName || "—"}</div>
                      </>
                    ) : (
                      <div><strong>דירוג:</strong> {pkg.stars || "—"}</div>
                    )}
                  </div>

                  <div className="public-offer-compare-card__price">
                    מחיר סופי: {formatIls(pkg.finalPriceIls)}
                  </div>

                  <Button
                    type="primary"
                    block
                    disabled
                  >
                    הזמן עכשיו
                  </Button>
                  <div className="public-offer-compare-card__booking-soon">
                    הזמנה אונליין תיפתח בקרוב
                  </div>

                  <Button
                    type="default"
                    block
                    onClick={() => onRequestAgentMessage(pkg, vm.offer_type)}
                    loading={selectingPackageId === (pkg.packageId || pkg.hotelId)}
                    disabled={
                      selectingPackageId != null &&
                      selectingPackageId !== (pkg.packageId || pkg.hotelId)
                    }
                  >
                    {selectedPackageId === (pkg.packageId || pkg.hotelId)
                      ? "הבקשה נשלחה לסוכן"
                      : "שלח הודעה לסוכן"}
                  </Button>
                </div>
              </section>
            ))}
          </div>
          {compareSelectionNotice ? (
            <div className="public-offer-page__compare-selection-notice">
              {compareSelectionNotice}
            </div>
          ) : null}

          <p className="public-offer-page__legal">{LEGAL_NOTE}</p>
          {branding ? (
            <footer className="public-offer-compare-contact" dir="rtl">
              <p className="public-offer-compare-contact__title">
                לפרטים והזמנות ניתן ליצור קשר עם הסוכן
              </p>
              <div className="public-offer-compare-contact__rows">
                {branding.phone ? (
                  <a
                    href={`tel:${String(branding.phone).replace(/[^\d+]/g, "")}`}
                    className="public-offer-compare-contact__link"
                  >
                    טלפון: {branding.phone}
                  </a>
                ) : null}
                {branding.email ? (
                  <a
                    href={`mailto:${branding.email}`}
                    className="public-offer-compare-contact__link"
                  >
                    מייל: {branding.email}
                  </a>
                ) : null}
                {branding.whatsapp ? (
                  <a
                    href={`https://wa.me/${String(branding.whatsapp).replace(/[^\d]/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="public-offer-compare-contact__link"
                  >
                    וואטסאפ: {branding.whatsapp}
                  </a>
                ) : null}
              </div>
            </footer>
          ) : null}
        </article>
      </div>
    );
  }

  if (rendererSelection.renderer === PUBLIC_OFFER_RENDERERS.HOTEL_OFFER) {
    if (!publicHotelVm) {
      return (
        <div className="public-offer-page public-offer-page--center" dir="rtl">
          <Helmet>
            <title>{title}</title>
            <meta name="description" content="הצעת מלון ציבורית" />
          </Helmet>
          <Spin size="large" tip="טוען הצעת מלון…" />
        </div>
      );
    }
    return (
      <div
        className="public-offer-page public-offer-page--hotel-sale hotel-offer-sale-page orderSummery-wrapper--guest-sale"
        dir="rtl"
      >
        <Helmet>
          <title>{title}</title>
          <meta name="description" content={`הצעת מלון ${publicHotelVm.hotelName}`} />
        </Helmet>
        <HotelOfferSaleHero
          hotelName={publicHotelVm.hotelName}
          heroImage={publicHotelVm.hotelImage}
          rating={publicHotelVm.rating}
          checkIn={publicHotelVm.checkIn}
          checkOut={publicHotelVm.checkOut}
          nights={publicHotelVm.nights}
          destination={publicHotelVm.destination}
          tags={publicHotelVm.tags}
          onPrimaryCta={onInterest}
        />
        <section
          className="orderSummery-guest-sale-card orderSummery-guest-sale-description orderSummery-guest-sale-description--top"
          aria-label="אודות המלון"
        >
          <h3>אודות המלון</h3>
          <p>
            {publicHotelVm.description ||
              "מלון איכותי במיקום נוח עם חוויית אירוח מוקפדת. פרטים מלאים יוצגו בעת השלמת ההזמנה."}
          </p>
        </section>
        <div className="orderSummery-guest-sale-grid">
          <aside className="orderSummery-guest-sale-rail">
            <div className="orderSummery-guest-sale-rail-card">
              <p className="orderSummery-guest-sale-rail-eyebrow">הצעה אישית</p>
              <h2 className="orderSummery-guest-sale-rail-title">סיכום הצעה</h2>
              <div className="orderSummery-guest-sale-price-block">
                <span className="orderSummery-guest-sale-price-label">סה״כ ללקוח</span>
                <span className="orderSummery-guest-sale-price-value">
                  {publicHotelVm.totalPrice != null ? (
                    <span className="hotel-api-price-dual-line" dir="rtl">
                      <span className="hotel-api-price-dual-line__ils">
                        {formatIls(publicHotelVm.totalPrice)}
                      </span>
                      {publicHotelVm.totalPriceUsd != null &&
                      publicHotelVm.totalPriceUsd > 0 ? (
                        <span
                          className="hotel-api-price-dual-line__usd"
                          dir="ltr"
                          translate="no"
                        >
                          ($
                          {Math.ceil(publicHotelVm.totalPriceUsd).toLocaleString("en-US")})
                        </span>
                      ) : null}
                    </span>
                  ) : (
                    "—"
                  )}
                </span>
                <div className="orderSummery-guest-sale-price-badges">
                  <span className="orderSummery-guest-sale-price-badge">כולל מיסים</span>
                  <span className="orderSummery-guest-sale-price-badge">ללא עמלות נסתרות</span>
                  {publicHotelVm.nights ? (
                    <span className="orderSummery-guest-sale-price-badge">
                      {publicHotelVm.nights} לילות
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="orderSummery-guest-sale-cta-row">
                <button
                  type="button"
                  className="orderSummery-guest-sale-cta-primary"
                  onClick={onInterest}
                  disabled={interestBusy || interestDone}
                >
                  {interestDone ? "פנייה נשלחה" : "אני רוצה להזמין"}
                </button>
              </div>
              <ul className="orderSummery-guest-sale-trust-list" aria-label="אמון ובטחון">
                <li><span>תשלום מאובטח</span></li>
                <li><span>אישור ותמיכה אישית</span></li>
                <li><span>נציג מלווה עד סיום ההזמנה</span></li>
              </ul>
            </div>
          </aside>

          <main className="orderSummery-guest-sale-main">
            <HotelOfferSaleGallery
              images={
                publicHotelVm.galleryImages?.length
                  ? publicHotelVm.galleryImages
                  : [publicHotelVm.hotelImage].filter(Boolean)
              }
              hotelName={publicHotelVm.hotelName}
            />

            <section className="orderSummery-guest-sale-card orderSummery-guest-sale-details-row" aria-label="פרטי חבילה">
              <article>
                <div>
                  <p>תאריכים</p>
                  <strong>{publicHotelVm.dates}</strong>
                </div>
              </article>
              <article>
                <div>
                  <p>סוג חדר</p>
                  <strong>{publicHotelVm.roomName}</strong>
                </div>
              </article>
              <article>
                <div>
                  <p>פנסיון</p>
                  <strong>{publicHotelVm.board}</strong>
                </div>
              </article>
              <article>
                <div>
                  <p>תפוסה</p>
                  <strong>{publicHotelVm.passengers}</strong>
                </div>
              </article>
            </section>

            <section className="orderSummery-guest-sale-card orderSummery-guest-sale-highlights" aria-label="למה לבחור">
              <h3>למה לבחור בהצעה הזו</h3>
              <div className="orderSummery-guest-sale-highlights-grid">
                {[
                  ["מיקום נוח", publicHotelVm.destination || "קרוב לאזורי הבילוי והחופשה"],
                  ["חדר מתאים", publicHotelVm.roomName],
                  ["מחיר ברור", "הצעה מסודרת עם סיכום מחיר ותנאים"],
                ].map(([heading, text]) => (
                  <article key={heading} className="orderSummery-guest-sale-highlight-card">
                    <h4>{heading}</h4>
                    <p>{text}</p>
                  </article>
                ))}
              </div>
            </section>

            <section className="orderSummery-guest-sale-card orderSummery-guest-sale-room-card" aria-label="פרטי חדר">
              <header>
                <span className="orderSummery-guest-sale-room-badge">דיל משתלם</span>
                <h3>{publicHotelVm.roomName}</h3>
                <p>חוויית אירוח מוקפדת עם תכנון נוח, פרטיות וסטנדרט שירות גבוה.</p>
              </header>
              <div className="orderSummery-guest-sale-room-icons">
                <span>חדר נבחר</span>
                <span>{publicHotelVm.board}</span>
                <span>{publicHotelVm.passengers}</span>
              </div>
            </section>

            <section className="orderSummery-guest-sale-card orderSummery-guest-sale-terms" aria-label="תנאים ומדיניות">
              <h3>תנאים חשובים</h3>
              <details open>
                <summary>מדיניות ביטול</summary>
                <p>
                  {publicHotelVm.cancellationPolicy ||
                    "תנאי הביטול והשינוי כפופים למדיניות הספק ויוצגו לפני אישור סופי."}
                </p>
              </details>
              <details>
                <summary>תנאי תשלום</summary>
                <p>{publicHotelVm.legalNote}</p>
              </details>
            </section>
          </main>
        </div>
        <PublicOfferAgentContactFooter branding={publicHotelVm.agent} />
        {interestBusy || interestDone ? (
          <div className="public-offer-page__hotel-interest-state">
            {interestDone ? "תודה, פנייתך נרשמה" : "שולחים את הפנייה..."}
          </div>
        ) : null}
      </div>
    );
  }

  if (rendererSelection.renderer === PUBLIC_OFFER_RENDERERS.HERO_TEMPLATE_V1) {
    const branding = normalizeBranding(
      vm?.branding || offerPayload?.branding,
      offerPayload?.agent || offerPayload?.order?.agent || null
    );
    const customer = resolveCustomerDetails(offerPayload, vm);
    const campaignStyleOfferBase = hydrateDynamicPackageCustomerOffer({
      payload: offerPayload,
      viewModel: vm,
      branding,
      customerName: customer.customerName,
    });
    const campaignStyleOffer =
      campaignStyleOfferBase && remoteGalleryImages?.length
        ? {
            ...campaignStyleOfferBase,
            heroImage:
              remoteGalleryImages[0] || campaignStyleOfferBase.heroImage,
            roomImage:
              remoteGalleryImages[0] || campaignStyleOfferBase.roomImage,
            galleryImages: remoteGalleryImages.map((src, index) => ({
              id: `remote-gallery-${index}`,
              src,
              altHe: campaignStyleOfferBase.hotelName || "מלון",
            })),
          }
        : campaignStyleOfferBase;

    if (campaignStyleOffer) {
      return (
        <>
          <Helmet>
            <title>{title}</title>
            <meta
              name="description"
              content={vm?.header?.datesLine || "הצעת חבילת נופש"}
            />
          </Helmet>
          <CampaignCustomerOfferView
            offer={campaignStyleOffer}
            kicker={campaignStyleOffer.kicker}
            brandTitle={campaignStyleOffer.hotelName}
            primaryCta={{
              type: "leadForm",
              label: "שלחו פנייה לסוכן",
              title: "מעוניינים בהצעה?",
              hint: "השאירו שם וטלפון — הסוכן יחזור אליכם בהקדם",
              onSubmit: onPublicOfferLeadSubmit,
              loading: interestBusy,
              disabled: interestBusy || interestDone,
              done: interestDone,
              doneLabel: "תודה, פנייתך נרשמה — ניצור איתך קשר בהקדם",
              loadingLabel: "שולחים…",
              error: interestError,
            }}
            legalNote={LEGAL_NOTE}
            totalHint={
              campaignStyleOffer.hasFlights
                ? campaignStyleOffer.baggageLines?.length
                  ? "מחיר סופי לחבילה (מלון + טיסות + כבודה)"
                  : "מחיר סופי לחבילה (מלון + טיסות)"
                : "מחיר סופי לחבילה (מלון)"
            }
          />
        </>
      );
    }

    return (
      <PublicOfferHeroTemplate
        vm={vm}
        title={title}
        legalNote={LEGAL_NOTE}
        onInterest={onInterest}
        interestBusy={interestBusy}
        interestDone={interestDone}
        branding={branding}
      />
    );
  }

  return (
    <div className="public-offer-page public-offer-page--center" dir="rtl">
      <Helmet>
        <title>הצעה לא זמינה | HotelsRiver</title>
      </Helmet>
      <div className="public-offer-page__card public-offer-page__card--error">
        <h1 className="public-offer-page__error-title">ההצעה אינה זמינה</h1>
        <p className="public-offer-page__error-text">פורמט הצעה לא נתמך.</p>
      </div>
    </div>
  );
}
